<template>
  <div class="feedback-buttons-wrapper">
    <button
      type="button"
      class="feedback-button"
      :aria-disabled="isFeedbackDisabled"
      :aria-label="$t('search.likeButtonAriaLabel')"
      @click.stop="handleFeedback('positive')"
    >
      <Icon
        name="lucide:thumbs-up"
        size="14"
        mode="svg"
        :class="{ 'icon-active': isLiked }"
      />
    </button>
    <button
      type="button"
      class="feedback-button"
      :aria-disabled="isFeedbackDisabled"
      :aria-label="$t('search.dislikeButtonAriaLabel')"
      @click.stop="handleFeedback('negative')"
    >
      <Icon
        name="lucide:thumbs-down"
        size="14"
        mode="svg"
        :class="{ 'icon-active': isDisliked }"
      />
    </button>

    <div
      v-if="isPopoverActive"
      ref="feedbackPopoverRef"
      class="feedback-popover"
      role="region"
      :aria-labelledby="`feedback-popover-title-${id}`"
      tabindex="-1"
    >
      <span
        :id="`feedback-popover-title-${id}`"
        class="title"
      >
        {{ $t('search.thankYou') }}
      </span>
      <span class="description">{{ $t('search.yourFeedbackHelpsUsImprove') }}</span>
      <div class="buttons-wrapper">
        <button
          ref="shareMoreFeedbackButtonRef"
          type="button"
          :aria-label="$t('search.shareMoreFeedback')"
          @click.stop="openFeedbackModal"
        >
          {{ $t('search.shareMoreFeedback') }}
        </button>
        <button
          type="button"
          :aria-label="$t('search.close')"
          @click.stop="closeFeedbackPopover"
        >
          {{ $t('search.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useFeedback } from '@/composables/useFeedback';
import { useStore } from 'vuex';
import { ref, computed, watch, onUnmounted, useTemplateRef, nextTick } from 'vue';

const props = defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
  isContentLoading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const id = useId();
const isLiked = ref(false);
const isDisliked = ref(false);

// Refs
const lastActiveElementRef = ref(null);
const shareMoreFeedbackButtonRef = useTemplateRef('shareMoreFeedbackButtonRef');
const feedbackPopoverRef = useTemplateRef('feedbackPopoverRef');

// Staged feedback (pending submission)
const localFeedbackData = ref(null);
const localFeedbackSentiment = ref(null);

// Backend feedback submission tracking
const hasSubmittedLike = ref(false);
const hasSubmittedDislike = ref(false);

const isPopoverActive = computed(() => store.getters.getActiveFeedbackPopoverId === id);
const isFeedbackDisabled = computed(() => props.isContentLoading || feedbackLoading.value);

const emits = defineEmits(['openFeedbackModal']);
const { isLoading: feedbackLoading, submitFeedback, deleteFeedback } = useFeedback();

const popoverTimer = ref(null);

watch(isPopoverActive, async (newValue, oldValue) => {
  if (newValue === true) {
    lastActiveElementRef.value = document.activeElement;
    await nextTick();
    shareMoreFeedbackButtonRef.value?.focus();
    popoverTimer.value = setTimeout(() => {
      if (feedbackPopoverRef.value.contains(document.activeElement)) {
        lastActiveElementRef.value?.focus();
      }
      store.commit('clearActiveFeedbackPopover');
    }, 5000);
  } else if (oldValue === true && newValue === false) {
    clearTimeout(popoverTimer.value);

    if (localFeedbackData.value && localFeedbackSentiment.value) {
      submitFeedback(localFeedbackData.value, localFeedbackSentiment.value);

      if (localFeedbackSentiment.value === 'positive') {
        hasSubmittedLike.value = true;
        hasSubmittedDislike.value = false;
      } else {
        hasSubmittedDislike.value = true;
        hasSubmittedLike.value = false;
      }
    }
    localFeedbackData.value = null;
    localFeedbackSentiment.value = null;
  }
});
onUnmounted(() => {
  clearTimeout(popoverTimer.value);
});

const handleFeedback = async (sentiment) => {
  const isPositive = sentiment === 'positive';
  const currentRef = isPositive ? isLiked : isDisliked;
  const oppositeRef = isPositive ? isDisliked : isLiked;

  const hasSubmittedCurrent = isPositive ? hasSubmittedLike : hasSubmittedDislike;
  const hasSubmittedOpposite = isPositive ? hasSubmittedDislike : hasSubmittedLike;
  const wasFeedback = currentRef.value;
  const wasOppositeFeedback = oppositeRef.value;

  currentRef.value = !currentRef.value;

  if (currentRef.value) {
    oppositeRef.value = false;

    // Delete opposite only if it was actually submitted to backend
    if (wasOppositeFeedback && hasSubmittedOpposite.value && props.result?.id && props.result?.type) {
      const oppositeSentiment = isPositive ? 'negative' : 'positive';
      deleteFeedback(props.result, oppositeSentiment);
      hasSubmittedOpposite.value = false;
    }

    // Submit the new feedback
    if (props.result?.id && props.result?.type) {
      localFeedbackData.value = props.result;
      localFeedbackSentiment.value = sentiment;
      store.commit('setFeedbackData', props.result);
      store.commit('setFeedbackSentiment', sentiment);
    }
    store.commit('setActiveFeedbackPopoverId', id);
  } else if (wasFeedback && props.result?.id && props.result?.type) {
    // Deactivating feedback (clicking same button again)
    if (hasSubmittedCurrent.value) {
      // Was submitted to backend → delete it
      await deleteFeedback(props.result, sentiment);
      hasSubmittedCurrent.value = false;
    } else {
      // Was only staged locally → cancel it
      localFeedbackData.value = null;
      localFeedbackSentiment.value = null;
      store.commit('clearActiveFeedbackPopover');
    }
  }
};

const openFeedbackModal = () => {
  emits('openFeedbackModal');
  store.commit('clearActiveFeedbackPopover');
};

const closeFeedbackPopover = () => {
  store.commit('clearActiveFeedbackPopover');
  lastActiveElementRef.value?.focus();
};
</script>

<style lang="scss" scoped>
.feedback-buttons-wrapper {
  position: relative;
  display: flex;
  gap: 12px;

  button {
    border-radius: 4px;
    background: none;
    border: none;
    color: inherit;
    padding: 0;
    transition: all 0.2s ease;
    position: relative;
    cursor: pointer;
  }

  .feedback-button {
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: var(--color-grey-500);
      transform: scale(1.1);
    }
  }

  .icon-active {
    color: var(--primary-button-background);
    animation: iconActivate 0.4s ease-out;
    transform: scale(1.1);
  }
}

.feedback-popover {
  --color-gray-200: #d3d3d3;
  --color-text-primary: #2d2d2d;
  --color-text-secondary: #474747;

  position: absolute;
  top: 30px;
  right: 0;
  width: 264px;
  height: 120px;
  padding: 10px;
  background-color: var(--primary-white);
  border: 1px solid var(--color-gray-200);
  z-index: 10;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  .title {
    display: block;
    letter-spacing: 0;
    color: var(--color-text-primary);
    margin-bottom: 8px;
  }

  .description {
    color: var(--color-text-secondary);
    line-height: 22px;
    margin-bottom: 8px;
    display: block;
  }

  .buttons-wrapper {
    display: flex;
    gap: 24px;

    button {
      color: var(--primary-button-background);
      font-size: 16px;
      cursor: pointer;
    }
  }
}

@keyframes iconActivate {
  0% {
    transform: scale(1);
    color: #2d2d2d;
  }
  50% {
    transform: scale(1.4);
    color: var(--primary-button-background);
  }
  100% {
    transform: scale(1.1);
    color: var(--primary-button-background);
  }
}
</style>
